<template>
    <div class="m-1 w-100">
        <go-lotus-side-menu
            :side-menu-items="menuItems"
            @set-parent-prop="propSetFromChild($event)"
            :hide-desktop="true"
        >
        </go-lotus-side-menu>

        <div class="w-100 d-flex justify-content-end align-items-center px-3">
            <p class="mb-0 mr-2"><b>Session date: </b></p>
            <v-date-picker popover-align="left" v-model="date"></v-date-picker>
        </div>

        <video ref="video" width="1" playsinline autoplay muted loop>
            <source src="/videos/px.webm" type="video/webm" />
            <source src="/videos/px.mp4" type="video/mp4" />
        </video>

        <div v-if="iOS" class="">
            <i class="small fa fa-exclamation-triangle text-warning mr-2"></i>
            <span class="font-italic small"
                >Looks like you're using iOS. We recommend disabling your device's auto-lock feature
                to prevent loss of data when locked.</span
            >
        </div>

        <template v-for="p in patients">
            <div class="row">
                <div class="col-12">
                    <session-patient
                        :user="user"
                        :date="date"
                        :key="p.id"
                        :patient="p"
                        :organization="organization"
                        :fade="fade"
                        :customizations="customizations"
                        @remove-patient="removePatient($event)"
                        @goal-detail="fetchGoal(p.id, $event)"
                        @close-tracker="closeTracker($event[0], $event[1])"
                    >
                        <div class="row">
                            <div class="col-12">
                                <p
                                    class="font-italic"
                                    data-private="lipsum"
                                    v-if="!p.selectedGoals || !p.selectedGoals.length"
                                >
                                    Please select the goals to be tracked for {{ p.name }}.
                                </p>
                                <span class="add-goal position-relative">
                                    <span
                                        @click="toggleShowAddGoal(p.id)"
                                        class="font-italic d-flex align-items-center cursor-pointer"
                                    >
                                        <i
                                            class="fa fa-plus-square-o fa-2x cursor-pointer mr-2"
                                        ></i>
                                        <span class="text-underline">Add Goal</span>
                                    </span>
                                    <transition name="simple-fade">
                                        <div
                                            v-if="showAddGoal === p.id"
                                            class="goal-add p-3 position-relative shadow bg-white"
                                        >
                                            <i
                                                class="position-absolute fa fa-times-circle-o cursor-pointer"
                                                @click="showAddGoal = null"
                                                style="top: 1rem; right: 1rem;"
                                            ></i>
                                            <p
                                                class="font-weight-bold text-uppercase"
                                                data-private="lipsum"
                                            >
                                                {{ p.name }} goal list
                                            </p>
                                            <p v-if="loading" class="font-italic my-3">
                                                <i
                                                    class="fa fa-spin fa-spinner fa-circle-o-notch mr-2"
                                                ></i
                                                >Loading goals...
                                            </p>
                                            <p
                                                v-else-if="!p.allGoals || !p.allGoals.length"
                                                class="font-italic my-3"
                                            >
                                                This patient does not have tracked goals.
                                                <a
                                                    class="font-italic text-underline"
                                                    :href="`/patients/${p.id}/goals`"
                                                    >Create one now.</a
                                                >
                                            </p>
                                            <ul
                                                class="goal-add-list d-flex flex-column flex-wrap pl-0"
                                                v-if="p.allGoals"
                                            >
                                                <li
                                                    class="mb-1"
                                                    v-for="(g, i) in p.allGoals.filter(
                                                        eachGoal =>
                                                            eachGoal.domain.name === 'Behavior'
                                                    )"
                                                    :style="{
                                                        borderLeft:
                                                            'solid 4px ' + g.domain
                                                                ? g.domain.color
                                                                : 'gray',
                                                    }"
                                                >
                                                    <div class="pl-2 d-flex align-items-center">
                                                        <input
                                                            type="checkbox"
                                                            class="mr-2"
                                                            :value="g.id"
                                                            :dusk="`track-goal-${g.id}`"
                                                            v-model="p.selectedGoals"
                                                        />
                                                        <span>{{ g.title }}</span>
                                                    </div>
                                                </li>
                                                <li
                                                    class="mb-1"
                                                    v-for="(g, i) in p.allGoals.filter(
                                                        eachGoal =>
                                                            eachGoal.domain.name !== 'Behavior'
                                                    )"
                                                    :style="{
                                                        borderLeft:
                                                            'solid 4px ' + g.domain
                                                                ? g.domain.color
                                                                : 'gray',
                                                    }"
                                                >
                                                    <div class="pl-2 d-flex align-items-center">
                                                        <input
                                                            type="checkbox"
                                                            class="mr-2"
                                                            :value="g.id"
                                                            :dusk="`track-goal-${g.id}`"
                                                            v-model="p.selectedGoals"
                                                        />
                                                        <span>{{ g.title }}</span>
                                                    </div>
                                                </li>
                                            </ul>
                                            <div class="d-flex w-100 justify-content-between mt-2">
                                                <button
                                                    @click.prevent="
                                                        allGoalsSelected(p) ? null : addAllGoals(p)
                                                    "
                                                    :class="
                                                        allGoalsSelected(p)
                                                            ? 'cursor-not-allowed text-muted'
                                                            : 'text-primary cursor-pointer'
                                                    "
                                                    class="btn btn-link font-weight-bold text-right text-uppercase"
                                                >
                                                    <i class="fa fa-plus-circle mr-2"></i>Add all
                                                    goals
                                                </button>
                                                <button
                                                    @click.prevent="
                                                        anyGoalSelected(p)
                                                            ? trackPatientGoals(p)
                                                            : null
                                                    "
                                                    :class="
                                                        anyGoalSelected(p)
                                                            ? 'text-primary cursor-pointer'
                                                            : 'cursor-not-allowed text-muted'
                                                    "
                                                    dusk="start-tracking"
                                                    class="btn btn-link font-weight-bold text-right text-uppercase"
                                                >
                                                    Start tracking
                                                    <i class="fa fa-arrow-circle-right"></i>
                                                </button>
                                            </div>
                                        </div>
                                    </transition>
                                </span>
                            </div>
                        </div>
                    </session-patient>
                </div>
            </div>
            <hr class="bar my-4" />
        </template>

        <div class="row">
            <div class="col-12">
                <p class="font-italic mt-4">
                    Please add a patient to the session to start tracking:
                </p>
            </div>
            <div class="d-flex flex-wrap mb-5">
                <div
                    class="patient-button cursor-pointer d-flex justify-content-start bg-white border-light rounded m-1 p-1"
                    v-for="c in unselectedSortedClients"
                    @click="addChild(c)"
                    :dusk="`patient-${c.id}`"
                >
                    <div>
                        <img
                            class="profile-image"
                            :src="c.profile_photo_url || '../../images/default_user.jpeg'"
                        />
                    </div>
                    <div class="d-flex ml-2 justify-content-between align-items-center w-100">
                        <span class="small" data-private="lipsum">{{ c.name }}</span>
                        <span class="text-uppercase font-weight-bold text-white small add-hover"
                            >Add</span
                        >
                    </div>
                </div>
            </div>
        </div>

        <!-- Modal -->
        <modal-component
            :id="'session-modal'"
            :visible="modal.visible"
            :title="modal.title"
            :size="modal.size"
            @close="resetModal()"
        >
            <div v-if="modal.type === 1" class="p-3">
                <component
                    v-if="goal"
                    :is="component"
                    :goal="goal"
                    :customizations="customizations"
                ></component>
            </div>
        </modal-component>
    </div>
</template>

<script>
import GoLotusSideMenu from "../components/GoLotusSideMenu";
import SessionPatient from "./tracker/SessionPatient";
import Detail from "./goals/Detail";
import DetailProbe from "./goals/DetailProbe";
import ModalComponent from "./ui/ModalComponent";
import { sideMenuItems } from "../plugins/sideMenuItems";

export default {
    name: "TrackerSession",
    props: ["userClients", "user", "organization"],
    components: {
        GoLotusSideMenu,
        SessionPatient,
        DetailProbe,
        Detail,
        ModalComponent,
    },
    mixins: [sideMenuItems],
    data() {
        return {
            date: new Date(),
            showAddChild: false,
            showAddGoal: null,
            patients: [],
            customizations: [],
            fade: null,
            goal: null,
            component: null,
            loading: false,
            modal: {
                visible: false,
                size: "",
                type: null,
                title: "",
            },
            fetchedCustomizations: false,
            iOS: false,
        };
    },

    mounted() {
        this.iOS = !!navigator.platform && /iPad|iPhone|iPod/.test(navigator.platform);
    },

    watch: {
        user() {
            if (this.user && this.organization && !this.fetchedCustomizations) {
                this.getTrackerCustomizations();
                this.fetchedCustomizations = true;
            }
        },
        organization() {
            if (this.user && this.organization && !this.fetchedCustomizations) {
                this.getTrackerCustomizations();
                this.fetchedCustomizations = true;
            }
        },
        patients() {
            if (this.patients.length) {
                window.onbeforeunload = () => {
                    if (
                        this.patients.some(
                            patient => patient.selectedGoals && patient.selectedGoals.length
                        )
                    ) {
                        return true;
                    } else {
                        window.onbeforeunload = null;
                    }
                };
            } else {
                window.onbeforeunload = null;
            }
        },
    },
    computed: {
        unselectedSortedClients() {
            return this.userClients
                ? this.userClients
                      .filter(c => !this.patients.map(p => p.id).includes(c.id))
                      .sort((a, b) =>
                          a.name.toLowerCase() > b.name.toLowerCase()
                              ? 1
                              : a.name.toLowerCase() < b.name.toLowerCase()
                              ? -1
                              : 0
                      )
                : [];
        },
    },
    methods: {
        addAllGoals(p) {
            p.selectedGoals = [];
            if (p.allGoals && p.allGoals.length) {
                p.allGoals.forEach(g => p.selectedGoals.push(g.id));
            }
        },

        anyGoalSelected(p) {
            return p.selectedGoals && p.selectedGoals.length;
        },

        allGoalsSelected(p) {
            return p.selectedGoals && p.allGoals && p.selectedGoals.length === p.allGoals.length;
        },

        getTrackerCustomizations() {
            axios
                .get(
                    `/api/users/${this.user.id}/tracker_customizations?organization=${
                        this.organization.id
                    }`
                )
                .then(r => {
                    this.customizations = r.data.data;
                })
                .catch(error => {
                    console.error(error);
                });
        },
        fetchGoal(patient_id, goal_id) {
            this.goal = null;
            axios.get(`/api/patients/${patient_id}/goals/${goal_id}`).then(response => {
                this.goal = response.data.data;
                this.setGoalComponent();
                this.openGoalModal();
                setTimeout(this.removePickerMinWidth, 300);
            });
        },
        setGoalComponent() {
            switch (this.goal.tracker_type) {
                case "cold_probe":
                    this.component = "detail-probe";
                    break;
                default:
                    this.component = "detail";
            }
        },
        openGoalModal() {
            this.modal.type = 1;
            this.modal.size = "lg";
            this.modal.title = this.goal.title || "Goal Details";
            this.modal.visible = true;
        },

        resetModal() {
            this.modal = {
                visible: false,
                size: "",
                type: null,
                title: "",
            };
        },
        addChild(c) {
            this.patients.push(c);
            this.getGoalsData(c.id);
            this.showAddChild = false;
            this.showAddGoal = c.id;
        },
        removePickerMinWidth() {
            let el = document.querySelectorAll("input[placeholder='MM/DD/YYYY']");
            if (el.length) {
                el.forEach(e => {
                    e.style.border = "none";
                    e.style.minWidth = "unset";
                    e.style.maxWidth = "5rem";
                });
            }
        },
        getGoalsData(id) {
            this.loading = true;
            axios.get(`/api/patients/${id}/goals/`).then(response => {
                this.$set(
                    this.patients.find(p => p.id === id),
                    "allGoals",
                    response.data.data.filter(g => !g.is_archived)
                );
                this.$set(this.patients.find(p => p.id === id), "selectedGoals", []);
                this.loading = false;
            });
        },
        toggleShowAddChild() {
            this.showAddChild = !this.showAddChild;
            this.showAddGoal = null;
        },
        toggleShowAddGoal(id) {
            this.showAddGoal === id ? (this.showAddGoal = null) : (this.showAddGoal = id);
            this.showAddChild = false;
        },
        trackPatientGoals(patient) {
            if (!patient.selectedGoals.length) {
                return;
            }
            mixpanel.track("Data Trackers - Track");
            let p = this.patients.filter(p => p.id === patient.id);
            let i = p ? this.patients.indexOf(p) : 0;
            if (i) {
                this.patients[i] = patient;
            } else {
                this.patients.pull(patient);
            }
            this.$emit("set-patients", this.patients);
            this.$bus.$emit("started-tracking", patient.id);
            this.showAddGoal = null;
        },
        removePatient(id) {
            let p = this.patients.find(p => p.id === id);
            let i = this.patients.indexOf(p);
            this.patients.splice(i, 1);
        },
        closeTracker(pid, gid) {
            this.fade = "slow-fade";

            Vue.nextTick().then(() => {
                let p = this.patients.find(p => p.id === pid);
                let i = p.selectedGoals.indexOf(gid);
                p.selectedGoals.splice(i, 1);
                this.fade = "simple-fade";
            });
        },
    },
};
</script>

<style scoped lang="scss">
.shadow {
    box-shadow: 0 0 3px 3px rgba(128, 128, 128, 0.4);
}
.profile-image-round {
    min-width: 3rem;
    max-width: 3rem;
    min-height: 3rem;
    max-height: 3rem;
    border-radius: 50%;
}
.profile-image {
    min-width: 1.5rem;
    max-width: 1.5rem;
    min-height: 1.5rem;
    max-height: 1.5rem;
}
.child-add {
    min-width: 20rem;
    max-width: 20rem;
    left: 0;
    top: 40px;
    z-index: 4;
    columns: 2;
    column-gap: 0;
    > li:hover {
        background-color: #d64571;
        color: white;
        font-weight: 500;
    }
}
.child-add:after {
    border: 10px solid;
    border-color: transparent transparent #fff transparent;
    content: "";
    height: 0;
    width: 0;
    position: absolute;
    left: 5px;
    top: -20px;
    z-index: 6;
}
.goal-add {
    min-width: 20rem;
    max-width: 20rem;
    left: 0;
    z-index: 4;
}
.goal-add-list {
    //columns: 2;
    //column-gap: 0;
    list-style: none;
}
.goal-add:after {
    border: 10px solid;
    border-color: transparent transparent #fff transparent;
    content: "";
    height: 0;
    width: 0;
    position: absolute;
    left: 5px;
    top: -20px;
    z-index: 6;
}

.patient-button {
    width: 12rem;
}

.patient-button:hover {
    box-shadow: 0 0 0 2px #d64571;
    > div > .add-hover {
        color: #d64571 !important;
    }
}

@media screen and (min-width: 576px) {
    .goal-add {
        min-width: 30rem;
        max-width: 30rem;
    }
    .goal-add-list {
        //columns: 3;
    }
}
</style>
